import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SetupWizardProvider from './SetupWizardProvider';
import SetupWizardHeader from './SetupWizardHeader';
import SetupWizardSidebar from './SetupWizardSidebar';
import Step1Organization from './steps/Step1Organization';
import Step2ClinicalStructure from './steps/Step2ClinicalStructure';
import Step3Users from './steps/Step3Users';
import Step4BiologicalEntities from './steps/Step4BiologicalEntities';
import Step5PolicyFramework from './steps/Step5PolicyFramework';
import Step6ProcessRegistry from './steps/Step6ProcessRegistry';
import Step7Protocols from './steps/Step7Protocols';
import Step8Risks from './steps/Step8Risks';
import Step9Compliance from './steps/Step9Compliance';
import Step10Tasks from './steps/Step10Tasks';
import Step11Workflows from './steps/Step11Workflows';
import Step12Review from './steps/Step12Review';
import Step13Completion from './steps/Step13Completion';
import './SetupWizard.css';

/**
 * Setup Wizard - Main Component
 * Guided first-time configuration for new hospital installations
 */
const SetupWizard = ({ userData, userName, structure, setStructure, onComplete, onOrganizationAttached }) => {
  const { t } = useTranslation('setup');
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepData, setStepData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const TOTAL_STEPS = 13;

  // Load saved progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('setupWizardProgress');
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        setCurrentStep(progress.currentStep || 1);
        setCompletedSteps(progress.completedSteps || []);
        setStepData(progress.stepData || {});
      } catch (error) {
        console.error('Error loading saved progress:', error);
      }
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    const progress = {
      currentStep,
      completedSteps,
      stepData,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('setupWizardProgress', JSON.stringify(progress));
  }, [currentStep, completedSteps, stepData]);

  const handleStepComplete = (stepNumber, data) => {
    setStepData(prev => ({
      ...prev,
      [stepNumber]: data
    }));
    
    if (!completedSteps.includes(stepNumber)) {
      setCompletedSteps(prev => [...prev, stepNumber]);
    }
    
    // Clear validation errors for this step
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[stepNumber];
      return next;
    });
  };

  const handleStepValidation = (stepNumber, errors) => {
    if (errors && errors.length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        [stepNumber]: errors
      }));
    } else {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[stepNumber];
        return next;
      });
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Check if step has minimum requirements that must be met before skipping
    const stepDataForCurrent = stepData[currentStep] || {};
    
    // Steps that require minimum data before skipping
    const stepsRequiringData = [7, 8, 9, 10, 11]; // Protocols, Risks, Compliance, Tasks, Workflows
    
    if (stepsRequiringData.includes(currentStep)) {
      // Check if step has minimum requirements met
      if (!stepDataForCurrent.requirementsMet && !stepDataForCurrent.skipped) {
        // Don't allow skip if requirements aren't met
        return;
      }
    }
    
    // Mark step as skipped
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }
    
    // Mark step data as skipped
    setStepData(prev => ({
      ...prev,
      [currentStep]: { ...stepDataForCurrent, skipped: true }
    }));
    
    handleNext();
  };

  const handleFinish = () => {
    // Clear saved progress
    localStorage.removeItem('setupWizardProgress');
    
    // Call completion callback
    if (onComplete) {
      onComplete();
    }
  };

  const canProceedToStep = (stepNumber) => {
    // Can always go to step 1
    if (stepNumber === 1) return true;
    
    // Check if previous steps are completed
    for (let i = 1; i < stepNumber; i++) {
      if (!completedSteps.includes(i)) {
        return false;
      }
    }
    
    return true;
  };

  const renderStep = () => {
    const commonProps = {
      stepData: stepData[currentStep] || {},
      onComplete: (data) => handleStepComplete(currentStep, data),
      onValidation: (errors) => handleStepValidation(currentStep, errors),
      userData,
      userName,
      structure,
      setStructure,
      onOrganizationAttached
    };

    switch (currentStep) {
      case 1:
        return <Step1Organization {...commonProps} />;
      case 2:
        return <Step2ClinicalStructure {...commonProps} />;
      case 3:
        return <Step3Users {...commonProps} />;
      case 4:
        return <Step4BiologicalEntities {...commonProps} />;
      case 5:
        return <Step5PolicyFramework {...commonProps} />;
      case 6:
        return <Step6ProcessRegistry {...commonProps} />;
      case 7:
        return <Step7Protocols {...commonProps} />;
      case 8:
        return <Step8Risks {...commonProps} />;
      case 9:
        return <Step9Compliance {...commonProps} />;
      case 10:
        return <Step10Tasks {...commonProps} />;
      case 11:
        return <Step11Workflows {...commonProps} />;
      case 12:
        return <Step12Review {...commonProps} />;
      case 13:
        return <Step13Completion {...commonProps} onFinish={handleFinish} />;
      default:
        return <div>Invalid step</div>;
    }
  };

  const getStepTitle = (stepNumber) => {
    return t(`stepTitles.${stepNumber}`, { defaultValue: `Step ${stepNumber}` });
  };

  const progressPercentage = Math.round((completedSteps.length / TOTAL_STEPS) * 100);

  return (
    <SetupWizardProvider
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      completedSteps={completedSteps}
      stepData={stepData}
      validationErrors={validationErrors}
      onStepComplete={handleStepComplete}
      onStepValidation={handleStepValidation}
    >
      <div className="setup-wizard">
        <SetupWizardHeader
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
          stepTitle={getStepTitle(currentStep)}
          progressPercentage={progressPercentage}
          completedSteps={completedSteps}
          validationErrors={validationErrors[currentStep]}
          canProceed={canProceedToStep(currentStep + 1)}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
          onFinish={handleFinish}
          isLastStep={currentStep === TOTAL_STEPS}
        />
        
        <div className="setup-wizard-container">
          <SetupWizardSidebar
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            completedSteps={completedSteps}
            validationErrors={validationErrors}
          />
          
          <div className="setup-wizard-content">
            {renderStep()}
          </div>
        </div>
      </div>
    </SetupWizardProvider>
  );
};

export default SetupWizard;

